/**
 * Cost Optimization Modeler Component
 * Comprehensive cost modeling for route optimization decisions
 */

import React, { useState, useMemo, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Slider,
  Switch,
  FormControlLabel,
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  AttachMoney,
  LocalGasStation,
  AccessTime,
  Person,
  Build,
  DirectionsCar,
  LocalShipping,
  Receipt,
  TrendingUp,
  TrendingDown,
  ShowChart,
  PieChart,
  BarChart,
  Calculate,
  CompareArrows,
  Save,
  Upload,
  Download,
  Print,
  Help,
  Warning,
  CheckCircle,
  Error,
  Info,
  Speed,
  Timer,
  Payment,
  AccountBalance,
  Edit,
  Add,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useVehicles } from '../../hooks/useVehicles';
import { useProject } from '../../contexts/ProjectContext';
import type { Vehicle } from '../../types/vrp';

interface CostCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  subcategories: {
    id: string;
    name: string;
    value: number;
    unit: string;
    editable: boolean;
    description?: string;
  }[];
}

interface CostScenario {
  id: string;
  name: string;
  description: string;
  categories: CostCategory[];
  totalCost: number;
  costPerUnit: number;
}

interface PenaltyCost {
  id: string;
  name: string;
  threshold: number;
  rate: number;
  type: 'fixed' | 'percentage' | 'progressive';
}

interface CostComparison {
  baseline: number;
  optimized: number;
  savings: number;
  savingsPercentage: number;
  breakdowns: {
    category: string;
    baseline: number;
    optimized: number;
    difference: number;
  }[];
}

interface CostOptimizationModelerProps {
  onSave?: (scenario: CostScenario) => void;
  initialScenario?: CostScenario;
}

export const CostOptimizationModeler: React.FC<CostOptimizationModelerProps> = ({
  onSave,
  initialScenario,
}) => {
  // Data hooks
  const { currentProject } = useProject();
  const { data: vehicles = [] } = useVehicles(currentProject?.id || 0);

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [costCategories, setCostCategories] = useState<CostCategory[]>([
    {
      id: 'fixed',
      name: 'Fixed Costs',
      icon: <AccountBalance />,
      subcategories: [
        { id: 'vehicle-lease', name: 'Vehicle Lease/Purchase', value: 500, unit: 'per day', editable: true },
        { id: 'insurance', name: 'Insurance', value: 150, unit: 'per day', editable: true },
        { id: 'driver-wages', name: 'Driver Base Wages', value: 200, unit: 'per day', editable: true },
        { id: 'facility', name: 'Facility Costs', value: 100, unit: 'per day', editable: true },
        { id: 'admin', name: 'Administrative', value: 50, unit: 'per day', editable: true },
      ],
    },
    {
      id: 'variable',
      name: 'Variable Costs',
      icon: <TrendingUp />,
      subcategories: [
        { id: 'fuel', name: 'Fuel', value: 2.5, unit: 'per km', editable: true },
        { id: 'maintenance', name: 'Maintenance', value: 0.5, unit: 'per km', editable: true },
        { id: 'tolls', name: 'Tolls & Fees', value: 0.3, unit: 'per km', editable: true },
        { id: 'driver-overtime', name: 'Driver Overtime', value: 30, unit: 'per hour', editable: true },
        { id: 'per-stop', name: 'Per Stop Handling', value: 5, unit: 'per stop', editable: true },
      ],
    },
    {
      id: 'carrier',
      name: 'Carrier Rates',
      icon: <LocalShipping />,
      subcategories: [
        { id: 'ltl', name: 'LTL (Less Than Truckload)', value: 0.15, unit: 'per kg', editable: true },
        { id: 'ftl', name: 'FTL (Full Truckload)', value: 1200, unit: 'per load', editable: true },
        { id: 'parcel', name: 'Parcel Service', value: 15, unit: 'per package', editable: true },
        { id: 'expedited', name: 'Expedited Service', value: 2.5, unit: 'multiplier', editable: true },
        { id: 'international', name: 'International Shipping', value: 0.25, unit: 'per kg', editable: true },
      ],
    },
    {
      id: 'penalty',
      name: 'Penalty Costs',
      icon: <Warning />,
      subcategories: [
        { id: 'late-delivery', name: 'Late Delivery', value: 50, unit: 'per hour', editable: true },
        { id: 'missed-window', name: 'Missed Time Window', value: 100, unit: 'per occurrence', editable: true },
        { id: 'damage', name: 'Product Damage', value: 500, unit: 'per incident', editable: true },
        { id: 'customer-complaint', name: 'Customer Complaints', value: 200, unit: 'per complaint', editable: true },
        { id: 'regulatory', name: 'Regulatory Violations', value: 1000, unit: 'per violation', editable: true },
      ],
    },
  ]);

  const [penaltyCosts, setPenaltyCosts] = useState<PenaltyCost[]>([
    { id: 'late-1', name: 'Late Delivery (0-2 hours)', threshold: 0, rate: 50, type: 'fixed' },
    { id: 'late-2', name: 'Late Delivery (2-4 hours)', threshold: 2, rate: 100, type: 'fixed' },
    { id: 'late-3', name: 'Late Delivery (4+ hours)', threshold: 4, rate: 200, type: 'progressive' },
    { id: 'util-low', name: 'Low Utilization (<50%)', threshold: 50, rate: 10, type: 'percentage' },
  ]);

  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [sensitivityAnalysis, setSensitivityAnalysis] = useState({
    fuelPrice: { min: -20, max: 20, current: 0 },
    laborCost: { min: -20, max: 20, current: 0 },
    demandVolume: { min: -30, max: 30, current: 0 },
  });

  // Calculate total costs
  const calculateTotalCosts = useMemo(() => {
    const totals = {
      fixed: 0,
      variable: 0,
      carrier: 0,
      penalty: 0,
      total: 0,
    };

    costCategories.forEach(category => {
      const categoryTotal = category.subcategories.reduce((sum, sub) => sum + sub.value, 0);
      totals[category.id as keyof typeof totals] = categoryTotal;
    });

    totals.total = totals.fixed + totals.variable + totals.carrier + totals.penalty;
    return totals;
  }, [costCategories]);

  // Generate cost breakdown chart data
  const costBreakdownData = useMemo(() => {
    return costCategories.map(category => ({
      name: category.name,
      value: category.subcategories.reduce((sum, sub) => sum + sub.value, 0),
      percentage: (category.subcategories.reduce((sum, sub) => sum + sub.value, 0) / calculateTotalCosts.total * 100).toFixed(1),
    }));
  }, [costCategories, calculateTotalCosts]);

  // Generate sensitivity analysis data
  const sensitivityData = useMemo(() => {
    const baselineCost = calculateTotalCosts.total;
    const data = [];
    
    for (let i = -30; i <= 30; i += 10) {
      data.push({
        change: i,
        fuel: baselineCost * (1 + (i / 100) * 0.3),
        labor: baselineCost * (1 + (i / 100) * 0.4),
        demand: baselineCost * (1 + (i / 100) * 0.2),
      });
    }
    
    return data;
  }, [calculateTotalCosts]);

  // Handle cost value changes
  const handleCostChange = (categoryId: string, subcategoryId: string, value: number) => {
    setCostCategories(prev =>
      prev.map(category =>
        category.id === categoryId
          ? {
              ...category,
              subcategories: category.subcategories.map(sub =>
                sub.id === subcategoryId
                  ? { ...sub, value }
                  : sub
              ),
            }
          : category
      )
    );
  };

  // Export cost model
  const exportCostModel = () => {
    const data = {
      project: currentProject?.name,
      date: new Date().toISOString(),
      categories: costCategories,
      totals: calculateTotalCosts,
      penalties: penaltyCosts,
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cost-model-${Date.now()}.json`;
    a.click();
  };

  // Import cost model
  const importCostModel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        if (data.categories) {
          setCostCategories(data.categories);
        }
        if (data.penalties) {
          setPenaltyCosts(data.penalties);
        }
      } catch (error) {
        console.error('Failed to import cost model:', error);
      }
    };
    reader.readAsText(file);
  };

  const COLORS = ['#14B8A6', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <Box>
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AttachMoney sx={{ fontSize: 32, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  Cost Optimization Modeler
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comprehensive cost analysis and optimization
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <input
                type="file"
                accept=".json"
                onChange={importCostModel}
                style={{ display: 'none' }}
                id="import-cost-model"
              />
              <label htmlFor="import-cost-model">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Upload />}
                  size="small"
                >
                  Import
                </Button>
              </label>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportCostModel}
                size="small"
              >
                Export
              </Button>
              <Button
                variant="outlined"
                startIcon={<Print />}
                onClick={() => window.print()}
                size="small"
              >
                Print
              </Button>
              {onSave && (
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={() => onSave({
                    id: Date.now().toString(),
                    name: 'Current Cost Model',
                    description: 'Saved cost configuration',
                    categories: costCategories,
                    totalCost: calculateTotalCosts.total,
                    costPerUnit: calculateTotalCosts.total / 100,
                  })}
                  size="small"
                >
                  Save Model
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Cost Categories" />
          <Tab label="Penalty Modeling" />
          <Tab label="Comparison Analysis" />
          <Tab label="Sensitivity Analysis" />
          <Tab label="ROI Calculator" />
        </Tabs>
      </Paper>

      {/* Cost Categories Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            {costCategories.map((category) => (
              <Paper key={category.id} elevation={1} sx={{ mb: 2 }}>
                <Box sx={{ p: 2, backgroundColor: '#F9FAFB' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {category.icon}
                    <Typography variant="h6">{category.name}</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="h6" color="primary">
                      ${category.subcategories.reduce((sum, sub) => sum + sub.value, 0).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {category.subcategories.map((subcategory) => (
                        <TableRow key={subcategory.id}>
                          <TableCell>{subcategory.name}</TableCell>
                          <TableCell align="right" sx={{ width: 200 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={subcategory.value}
                              onChange={(e) => handleCostChange(
                                category.id,
                                subcategory.id,
                                parseFloat(e.target.value) || 0
                              )}
                              disabled={!subcategory.editable}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">$</InputAdornment>
                                ),
                              }}
                              sx={{ width: '100%' }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 100 }}>
                            <Typography variant="caption" color="text.secondary">
                              {subcategory.unit}
                            </Typography>
                          </TableCell>
                          {subcategory.description && (
                            <TableCell sx={{ width: 40 }}>
                              <Tooltip title={subcategory.description}>
                                <Help fontSize="small" color="action" />
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Card elevation={2} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Cost Summary
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Fixed Costs" />
                    <ListItemSecondaryAction>
                      <Typography variant="h6" color="primary">
                        ${calculateTotalCosts.fixed.toFixed(2)}
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Variable Costs" />
                    <ListItemSecondaryAction>
                      <Typography variant="h6" color="primary">
                        ${calculateTotalCosts.variable.toFixed(2)}
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Carrier Rates" />
                    <ListItemSecondaryAction>
                      <Typography variant="h6" color="primary">
                        ${calculateTotalCosts.carrier.toFixed(2)}
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Penalty Costs" />
                    <ListItemSecondaryAction>
                      <Typography variant="h6" color="warning.main">
                        ${calculateTotalCosts.penalty.toFixed(2)}
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary={
                        <Typography variant="h6">Total Cost</Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="h5" color="primary" fontWeight={600}>
                        ${calculateTotalCosts.total.toFixed(2)}
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Cost Breakdown
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={costBreakdownData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${entry.percentage}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {costBreakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Penalty Modeling Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Penalty Cost Modeling</AlertTitle>
              Define penalty costs for service level violations and operational inefficiencies.
            </Alert>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Penalty Rules
              </Typography>
              <List>
                {penaltyCosts.map((penalty) => (
                  <ListItem key={penalty.id}>
                    <ListItemIcon>
                      <Warning color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary={penalty.name}
                      secondary={`Threshold: ${penalty.threshold}, Rate: $${penalty.rate} (${penalty.type})`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              <Button
                variant="outlined"
                startIcon={<Add />}
                fullWidth
                sx={{ mt: 2 }}
              >
                Add Penalty Rule
              </Button>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Penalty Impact Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart
                  data={[
                    { hour: 0, cost: 0 },
                    { hour: 1, cost: 50 },
                    { hour: 2, cost: 100 },
                    { hour: 3, cost: 150 },
                    { hour: 4, cost: 200 },
                    { hour: 5, cost: 300 },
                    { hour: 6, cost: 450 },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" label={{ value: 'Hours Late', position: 'insideBottom', offset: -5 }} />
                  <YAxis label={{ value: 'Penalty Cost ($)', angle: -90, position: 'insideLeft' }} />
                  <RechartsTooltip />
                  <Area
                    type="stepAfter"
                    dataKey="cost"
                    stroke="#EF4444"
                    fill="#FEE2E2"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Comparison Analysis Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Cost Comparison: Private Fleet vs Carrier vs Direct Shipping
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <RechartsBarChart
                  data={[
                    {
                      scenario: 'Current',
                      privateFleet: 5000,
                      carrier: 6500,
                      directShipping: 8000,
                    },
                    {
                      scenario: 'Optimized',
                      privateFleet: 4200,
                      carrier: 6200,
                      directShipping: 7500,
                    },
                    {
                      scenario: 'Peak Season',
                      privateFleet: 6500,
                      carrier: 7800,
                      directShipping: 10000,
                    },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="scenario" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="privateFleet" fill="#14B8A6" name="Private Fleet" />
                  <Bar dataKey="carrier" fill="#F59E0B" name="Carrier Services" />
                  <Bar dataKey="directShipping" fill="#EF4444" name="Direct Shipping" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Sensitivity Analysis Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Cost Sensitivity Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={sensitivityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="change" 
                    label={{ value: 'Percentage Change (%)', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis 
                    label={{ value: 'Total Cost ($)', angle: -90, position: 'insideLeft' }}
                  />
                  <RechartsTooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="fuel" 
                    stroke="#14B8A6" 
                    name="Fuel Price"
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="labor" 
                    stroke="#3B82F6" 
                    name="Labor Cost"
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="demand" 
                    stroke="#F59E0B" 
                    name="Demand Volume"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Sensitivity Parameters
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Fuel Price Change
                </Typography>
                <Slider
                  value={sensitivityAnalysis.fuelPrice.current}
                  onChange={(_, value) => setSensitivityAnalysis(prev => ({
                    ...prev,
                    fuelPrice: { ...prev.fuelPrice, current: value as number }
                  }))}
                  min={-20}
                  max={20}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                />
              </Box>
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Labor Cost Change
                </Typography>
                <Slider
                  value={sensitivityAnalysis.laborCost.current}
                  onChange={(_, value) => setSensitivityAnalysis(prev => ({
                    ...prev,
                    laborCost: { ...prev.laborCost, current: value as number }
                  }))}
                  min={-20}
                  max={20}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                />
              </Box>
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Demand Volume Change
                </Typography>
                <Slider
                  value={sensitivityAnalysis.demandVolume.current}
                  onChange={(_, value) => setSensitivityAnalysis(prev => ({
                    ...prev,
                    demandVolume: { ...prev.demandVolume, current: value as number }
                  }))}
                  min={-30}
                  max={30}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                />
              </Box>
              
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Impact Summary
                </Typography>
                <Typography variant="body2">
                  • 10% fuel increase: +3% total cost<br/>
                  • 10% labor increase: +4% total cost<br/>
                  • 10% demand increase: +2% total cost
                </Typography>
              </Alert>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ROI Calculator Tab */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                ROI Calculator
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Initial Investment"
                    type="number"
                    defaultValue={100000}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Annual Cost Savings"
                    type="number"
                    defaultValue={25000}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Implementation Period (months)"
                    type="number"
                    defaultValue={6}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Payback Period Target (years)"
                    type="number"
                    defaultValue={3}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                ROI Analysis Results
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <TrendingUp color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="ROI"
                    secondary="Return on Investment"
                  />
                  <ListItemSecondaryAction>
                    <Typography variant="h6" color="success.main">
                      25%
                    </Typography>
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Timer />
                  </ListItemIcon>
                  <ListItemText
                    primary="Payback Period"
                    secondary="Time to recover investment"
                  />
                  <ListItemSecondaryAction>
                    <Typography variant="h6">
                      4 years
                    </Typography>
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AttachMoney />
                  </ListItemIcon>
                  <ListItemText
                    primary="NPV"
                    secondary="Net Present Value (5 years)"
                  />
                  <ListItemSecondaryAction>
                    <Typography variant="h6" color="primary">
                      $45,000
                    </Typography>
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ShowChart />
                  </ListItemIcon>
                  <ListItemText
                    primary="IRR"
                    secondary="Internal Rate of Return"
                  />
                  <ListItemSecondaryAction>
                    <Typography variant="h6">
                      18%
                    </Typography>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
              
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This optimization project meets the target payback period and provides positive ROI.
                </Typography>
              </Alert>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};